import { useEffect, useRef, useState } from "react";
import { Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const KEYS = ["business_name", "tagline", "phone", "support_email", "sender_email", "alerts_email", "statement_day", "address"];

export const StatementJobButton = ({ onDone, variant = "outline" }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/statements/run");
      toast.success(`Statement job: ${data.statements_created} created`, {
        description: `${data.loans_checked} loans checked · ${data.emails_sent} emailed · ${data.emails_failed} failed`,
      });
      onDone?.(data);
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant={variant} onClick={run} disabled={busy} data-testid="run-statement-job-button">
      {busy ? <Loader2 className="animate-spin" /> : <CalendarClock />} Run statement job
    </Button>
  );
};

export const BusinessSettings = () => {
  const [f, setF] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const edited = useRef(new Set());
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    api.get("/settings").then((r) => {
      const server = Object.fromEntries(KEYS.map((k) => [k, String(r.data[k] ?? "")]));
      setF((prev) => (prev ? Object.fromEntries(KEYS.map((k) => [k, edited.current.has(k) ? prev[k] : server[k]])) : server));
    });
  }, []);
  if (!f) return null;
  const set = (k) => (e) => {
    edited.current.add(k);
    const v = e.target.value;
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    const d = +f.statement_day;
    if (!Number.isInteger(d) || d < 1 || d > 28) { setErrors({ statement_day: "Statement day must be between 1 and 28" }); return; }
    setBusy(true); setErrors({});
    try {
      await api.put("/settings", f);
      toast.success("Settings saved");
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields); toast.error(message);
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, props = {}) => <Input id={k} value={f[k]} onChange={set(k)} className={`h-10 bg-card ${errors[k] ? "border-destructive" : ""}`} data-testid={`settings-${k.replace(/_/g, "-")}-input`} {...props} />;

  return (
    <form onSubmit={save} noValidate className="fade-up mb-10 rounded-lg border bg-card p-6 sm:p-8" data-testid="business-settings-form">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Business & statements</h3>
          <p className="mt-1 text-sm text-muted-foreground">Shown on statement PDFs and emails. Statements are generated on each loan's statement day (override per loan).</p>
        </div>
        <StatementJobButton />
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        <Field label="Business name" name="business_name" error={errors.business_name}>{inp("business_name")}</Field>
        <Field label="Tagline" name="tagline">{inp("tagline")}</Field>
        <Field label="Phone" name="phone">{inp("phone")}</Field>
        <Field label="Support email" name="support_email" error={errors.support_email} hint="Used as Reply-To">{inp("support_email")}</Field>
        <Field label="Sender email" name="sender_email" error={errors.sender_email} hint="Must be on a Resend-verified domain">{inp("sender_email")}</Field>
        <Field label="New application alerts" name="alerts_email" error={errors.alerts_email} hint="Admin email notified of new applications">{inp("alerts_email")}</Field>
        <Field label="Default statement day" name="statement_day" error={errors.statement_day} hint="1–28">{inp("statement_day", { inputMode: "numeric" })}</Field>
        <Field label="Business address" name="address" className="md:col-span-3">
          <Textarea id="address" rows={2} value={f.address} onChange={set("address")} data-testid="settings-address-input" />
        </Field>
      </div>
      <div className="mt-6 flex justify-end">
        <Button type="submit" disabled={busy} data-testid="settings-save-button">{busy && <Loader2 className="animate-spin" />} Save settings</Button>
      </div>
    </form>
  );
};
